import { useEffect, useState, useRef } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { XMarkIcon } from "@heroicons/react/24/outline";

const YouTubeModal = ({ isOpen, onClose, videoId = "FmGmtWvQmOg" }) => {
  const [isLoading, setIsLoading] = useState(true);
  const iframeRef = useRef(null);

  // build iframe src with origin and enablejsapi to avoid widget API origin mismatch
  const getIframeSrc = () => {
    const origin = typeof window !== "undefined" ? window.location.origin : "";
    const params = new URLSearchParams({
      autoplay: "1",
      rel: "0",
      modestbranding: "1",
      iv_load_policy: "3",
      enablejsapi: "1",
      origin,
    });
    // use youtube-nocookie for privacy but include origin param
    return `https://www.youtube-nocookie.com/embed/${videoId}?${params.toString()}`;
  };

  // show spinner until iframe loads or timeout
  useEffect(() => {
    if (!isOpen) return;
    setIsLoading(true);
    const timeout = setTimeout(() => setIsLoading(false), 4000);
    return () => clearTimeout(timeout);
  }, [isOpen]);

  // منع التمرير عند فتح المودال
  useEffect(() => {
    document.body.style.overflow = isOpen ? "hidden" : "auto";
    return () => {
      document.body.style.overflow = "auto";
    };
  }, [isOpen]);

  // cleanup: stop video when modal closes by removing iframe src
  useEffect(() => {
    if (!isOpen && iframeRef.current) {
      // remove src to stop playback and avoid widget messages after unmount
      try {
        iframeRef.current.src = "about:blank";
      } catch (e) {
        // ignore
      }
    }
  }, [isOpen]);

  return (
    <AnimatePresence>
      {isOpen && (
        <>
          {/* Backdrop */}
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.2 }}
            className="fixed inset-0 bg-black/80 backdrop-blur-sm z-40"
            onClick={onClose}
          />

          {/* Modal */}
          <motion.div
            initial={{ opacity: 0, scale: 0.95 }}
            animate={{ opacity: 1, scale: 1 }}
            exit={{ opacity: 0, scale: 0.95 }}
            transition={{
              duration: 0.3,
              type: "spring",
              stiffness: 300,
              damping: 25,
            }}
            className="fixed inset-0 z-50 flex items-center justify-center p-4"
          >
            <div className="relative w-full max-w-4xl">
              {/* Close Button */}
              <motion.button
                initial={{ opacity: 0, y: -10 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.2 }}
                onClick={onClose}
                className="absolute -top-12 right-0 text-white hover:text-gray-300 transition-colors z-50 bg-black/50 rounded-full p-2"
                aria-label="إغلاق الفيديو"
              >
                <XMarkIcon className="h-8 w-8" />
              </motion.button>

              {/* Video Container */}
              <div className="relative bg-black rounded-xl shadow-2xl overflow-hidden">
                {/* Loading Spinner Overlay */}
                <AnimatePresence>
                  {isLoading && (
                    <motion.div
                      initial={{ opacity: 1 }}
                      exit={{ opacity: 0 }}
                      transition={{ duration: 0.3 }}
                      className="absolute inset-0 bg-gray-900 flex items-center justify-center z-20"
                    >
                      <div className="text-center">
                        {/* Spinner */}
                        <div className="relative mb-6">
                          <div className="w-16 h-16 border-4 border-gray-700 rounded-full"></div>
                          <div className="w-16 h-16 border-4 border-t-blue-500 border-r-transparent border-b-transparent border-l-transparent rounded-full animate-spin absolute top-0 left-0"></div>
                        </div>

                        {/* Loading Text */}
                        <p className="text-gray-300 font-medium text-lg mb-2">
                          جاري تحميل الفيديو
                        </p>
                        <p className="text-gray-400 text-sm">
                          قد يستغرق بضع ثوانٍ
                        </p>
                      </div>
                    </motion.div>
                  )}
                </AnimatePresence>

                {/* Plyr Player */}
                <div
                  className={`transition-opacity duration-500 ${
                    isLoading ? "opacity-0" : "opacity-100"
                  }`}
                >
                  <div className="w-full aspect-video bg-black">
                    <iframe
                      ref={iframeRef}
                      title="YouTube video"
                      src={isOpen ? getIframeSrc() : "about:blank"}
                      className="w-full h-full"
                      frameBorder="0"
                      allow="autoplay; encrypted-media; fullscreen; picture-in-picture"
                      allowFullScreen
                      onLoad={() => setIsLoading(false)}
                    />
                  </div>
                </div>

                {/* Video Info (Optional) */}
                <div className="px-6 py-4 bg-gradient-to-t from-black/80 to-transparent">
                  <h3 className="text-white font-semibold text-lg">
                    فيلم توثيقي | جهاز مشروعات الخدمة الوطنية
                  </h3>
                  <p className="text-gray-400 text-sm mt-1">
                    إنجازات ومشروعات وطنية - 2025
                  </p>
                </div>
              </div>

              {/* Instructions */}
              <motion.div
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                transition={{ delay: 0.5 }}
                className="text-center mt-4 text-gray-400 text-sm"
              >
                اضغط خارج الفيديو أو زر الإغلاق للإغلاق
              </motion.div>
            </div>
          </motion.div>
        </>
      )}
    </AnimatePresence>
  );
};

export default YouTubeModal;
